// Custom client-side validators that we can use in our forms

// Based on https://gist.github.com/dperini/729294
const URL_REGEX = new RegExp(
  '^' +
    // protocol identifier
    '(?:(?:https?)://)' +
    // user:pass authentication
    '(?:\\S+(?::\\S*)?@)?' +
    '(?:' +
      // IP address exclusion
      // private & local networks
      '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
      '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
      '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
      // IP address dotted notation octets
      // excludes loopback network 0.0.0.0
      // excludes reserved space >= 224.0.0.0
      // excludes network & broacast addresses
      // (first & last IP address of each class)
      '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
      '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
      '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
    '|' +
      // host name
      '(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
      // domain name
      '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
      // TLD identifier
      '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,}))' +
      // TLD may end with dot
      '\\.?' +
    ')' +
    // port number
    '(?::\\d{2,5})?' +
    // resource path
    '(?:[/?#]\\S*)?' +
  '$', 'i');

const Validations = [];

// URL validation
Validations.push(($form, $field) => {
  const shouldValidate = $field.attr('type') === 'url';
  const fieldErrorText = window._locales.invalidUrlError;
  const formErrorText = window._locales.hintInvalidEntryError;
  let url = $field.val();

  // Prepend a protocol if necessary
  // (NOTE: This doesn't support protocol-relative URLs)
  if (url !== '' && !url.match(/^\w*:\/\//i)) {
    url = `http://${url}`;
  }
  if (shouldValidate && url !== '' && !url.match(URL_REGEX)) {
    $field.data('error', fieldErrorText);
    $form.data('error', formErrorText);
    return false;
  }
  return true;
});

// Presence validation
Validations.push(($form, $field) => {
  const shouldValidate = $field.hasClass('required');
  const fieldErrorText = window._locales.presenceError;
  const formErrorText = window._locales.hintPresenceError;
  // Radio buttons
  if (shouldValidate && $field.hasClass('input') && ($field.hasClass('radio_buttons') || $field.hasClass('yes_no'))) {
    if ($field.find('.radio_buttons:checked').length === 0 && $field.find('.yes_no:checked').length === 0) {
      $field.data('error', fieldErrorText);
      $form.data('error', formErrorText);
      return false;
    }
    return true;
  }
  // Check boxes
  if (shouldValidate && $field.hasClass('input') && $field.hasClass('check_boxes')) {
    if ($field.find('.check_boxes:checked').length === 0) {
      $field.data('error', fieldErrorText);
      $form.data('error', formErrorText);
      return false;
    }
    return true;
  }
  // Autocomplete react component
  if (shouldValidate && $field.parents('.AutocompleteInput').length > 0) {
    const component = $field.parents('.AutocompleteInput');
    if (component.find('[type=hidden][value!=""]').length === 0) {
      $field.data('error', fieldErrorText);
      $form.data('error', formErrorText);
      return false;
    }
    return true;
  }
  // Generic fields
  if (shouldValidate && $field.val() === '') {
    $field.data('error', fieldErrorText);
    $form.data('error', formErrorText);
    return false;
  }
  return true;
});

// Date validation
Validations.push(($form, $field) => {
  const shouldValidate = $field.parents('.validatableDateWidget').length;

  const fieldErrorText = window._locales.invalidDateError;
  const formErrorText = window._locales.hintInvalidEntryError;

  if (shouldValidate && $field.val() === '') {
    $field.data('error', fieldErrorText);
    $form.data('error', formErrorText);
    return false;
  }
  return true;
});

// Time validation
Validations.push(($form, $field) => {
  const shouldValidate = $field.parents('.validatableTimeWidget').length;

  const fieldErrorText = window._locales.invalidTimeError;
  const formErrorText = window._locales.hintInvalidEntryError;

  if (shouldValidate && $field.val() === '') {
    $field.data('error', fieldErrorText);
    $form.data('error', formErrorText);
    return false;
  }
  return true;
});

// Maximum length validation
Validations.push(($form, $field) => {
  const shouldValidate = !!$field.data('maxlength');
  const fieldErrorText = window._locales.tooLongError;
  const formErrorText = window._locales.hintInvalidEntryError;
  const text = $field.val();

  if (shouldValidate && text.length > $field.data('maxlength')) {
    $field.data('error', fieldErrorText);
    $form.data('error', formErrorText);
    return false;
  }
  return true;
});

/**
 * @param {string} fieldSelector Elements that will be validated. Default applies validations to all text inputs
 *   and textareas that have a `.required` class that are inside a form with a `data-validatable` attribute.
 * @param {string} wrapperErrorClass Class added to parent of the field. Default matches SimpleForm defaults.
 * @param {string} errorClass Class added to sibling of the field. Default matches SimpleForm defaults.
 */
export default class Validator {
  constructor({
    formSelector = 'form[data-validatable]',
    fieldSelector = [
      'input[type=text]', 'input[type=url]', 'input[type=number]',
      'input[type=email]', 'input[type=tel]', 'select', 'textarea',
      '.input.radio_buttons', '.input.yes_no', '.input.check_boxes',
      '.input.file',
    ].join(', '),
    wrapperErrorClass = 'field_with_errors',
    errorClass = 'error',
    submitSelector = '.Form-next, input[type=submit], [data-validatable-submit]',
    skipValidationSelector = '[data-skip-validation]',
    submitHintSelector = '[data-validatable-hint]',
  } = {}) {
    Object.assign(this, {
      formSelector,
      fieldSelector,
      wrapperErrorClass,
      errorClass,
      submitSelector,
      skipValidationSelector,
      submitHintSelector,
    });

    $(document).on('keypress change', `${formSelector} ${fieldSelector}`, e => this.handleInputChange(e));
    $(document).on('datetimeinput:change', `${formSelector} ${fieldSelector}`, e => this.handleCustomInputChange(e));
    $(document).on('autocompleteinput:change', `${formSelector} ${fieldSelector}`, e => this.handleCustomInputChange(e));
    $(document).on('blur', `${formSelector} ${fieldSelector}`, e => this.handleBlur(e));

    // Run on load
    this.toggleAllSubmits();
  }
  handleInputChange = (event) => {
    const $wrapper = $(event.target).parent();
    const $form = $wrapper.closest(this.formSelector);

    this.removeError($wrapper);
    this.toggleSubmit($form);
    return true;
  }
  handleCustomInputChange = (event) => {
    // Handle new input
    this.handleInputChange(event);
    // Simulate the field being blurred
    this.handleBlur(event);
    return true;
  }
  handleBlur = (event) => {
    const $field = $(event.target);
    const $wrapper = $field.parent();
    const $form = $wrapper.closest(this.formSelector);

    if (!this.fieldIsValid($form, $field)) {
      this.addError($wrapper, $field);
      this.toggleSubmit($form);
    }
    return true;
  }
  removeError($wrapper) {
    // Remove class from the wrapper element
    $wrapper.removeClass(this.wrapperErrorClass);

    // Remove error message if it exists
    $wrapper.find(`.${this.errorClass}`).remove();
  }
  addError($wrapper, $field) {
    if ($wrapper.hasClass('MarkdownInput')) {
      $wrapper = $wrapper.parent();
    }
    // Add a class to the wrapper element
    $wrapper.addClass(this.wrapperErrorClass);

    // If there isn't already an error message, add one. An error message may already
    // exist if the user focused and then onfocused a field without typing anything
    if (!$wrapper.find(`.${this.errorClass}`).length) {
      const $error = $('<span>').addClass(this.errorClass).html($field.data('error'));
      $wrapper.append($error);
    }
  }
  fieldIsValid($form, $field) {
    let valid = true;
    Validations.forEach((fun) => {
      valid = valid && fun($form, $field);
    });
    return valid;
  }
  formIsValid($form) {
    let valid = true;
    $form.find(this.fieldSelector).each((index, el) => {
      valid = valid && this.fieldIsValid($form, $(el));
    });
    return valid;
  }
  toggleSubmit($form) {
    if (this.formIsValid($form)) {
      this.enableSubmit($form);
    } else {
      this.disableSubmit($form);
    }
  }
  toggleAllSubmits() {
    $(this.formSelector).each((index, el) => {
      this.toggleSubmit($(el));
    });
  }
  enableSubmit($form) {
    const $submit = $form.find(this.submitSelector);
    $submit.removeClass('is-disabled').attr('disabled', false);
    $form.find(this.submitHintSelector).hide();
  }
  disableSubmit($form) {
    const $submit = $form.find(this.submitSelector).not(this.skipValidationSelector);
    $submit.addClass('is-disabled').attr('disabled', true);
    $form.find(this.submitHintSelector).text($form.data('error'));
    $form.find(this.submitHintSelector).show();
  }
}
